"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Compass, Sparkles, ShieldCheck } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
const pillars = [{
  icon: Sparkles,
  title: "Family legacy",
  description: "Milestone gatherings designed to honour shared heritage and create lasting traditions across generations."
}, {
  icon: Compass,
  title: "Generational harmony",
  description: "Anchor and orbit scheduling where grandparents, parents, and grandchildren travel effortlessly together."
}, {
  icon: ShieldCheck,
  title: "Logistical precision",
  description: "Impeccably coordinated private transfers, curated accommodations, and zero-friction travel management."
}];
export default function AboutIntro() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="aboutintroheader" data-nav="light" className="relative border-b border-border bg-background text-foreground py-16 md:py-24">
      <div className="container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl">
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1,
          y: 0
        } : {
          opacity: 0,
          y: 16
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          ease: "easeOut"
        }}>
            <span className="text-xs uppercase tracking-widest text-primary font-semibold">
              The Travel Design Studio
            </span>
            <h1 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-serif font-medium tracking-tight text-foreground">
              About E•Leete Travel Design
            </h1>
            <p className="mt-6 text-base sm:text-lg text-foreground/85 leading-relaxed font-sans">
              We serve as the mediator and logistical architect of Anchor &amp; Orbit multi-generational family itineraries. From milestone reunions to landmark anniversaries, our studio crafts bespoke travel experiences founded on family legacy, generational harmony, and logistical precision.
            </p>
          </motion.div>

          <motion.div initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          duration: 0.5,
          delay: 0.15
        }} className="mt-8 flex flex-wrap items-center gap-4">
            <Button asChild variant="default" size="lg" className="rounded-md">
              <Link to="/AboutDiscoveryConsultationPage">
                Schedule a consultation
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="rounded-md">
              <Link to="/AnchorOrbitMethodPage">
                Explore our method
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="rounded-md">
              <Link to="/FoundersPage">
                Meet our founders
              </Link>
            </Button>
          </motion.div>
        </div>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-8 pt-12 border-t border-border">
          {pillars.map((pillar, i) => {
          const IconComponent = pillar.icon;
          return <div key={pillar.title} data-index={i} className="flex flex-col gap-3">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-accent text-accent-foreground">
                    <IconComponent className="h-5 w-5" />
                  </div>
                  <h2 className="font-serif text-lg font-medium text-foreground">
                    {pillar.title}
                  </h2>
                </div>
                <p className="text-sm text-foreground/80 leading-relaxed font-sans">
                  {pillar.description}
                </p>
              </div>;
        })}
        </div>
      </div>
    </section>;
}